import { defineStore } from 'pinia'
import labelMappingService from '@/api/services/labelMapping'

export const useLabelMappingStore = defineStore('labelMapping', {
  state: () => ({
    // Current active label mappings (exposure_type -> custom_label)
    mappings: {},
    // All stored mappings with metadata
    allMappings: [],
    // Loading states
    isLoading: false,
    isUpdating: false,
    // Error state
    error: null,
    // Last loaded timestamp
    lastLoaded: null,
  }),

  getters: {
    /**
     * Get a custom label for an exposure type, fallback to formatted exposure type
     * @param {string|null} exposureType - The exposure type (e.g., 'interest_rate')
     * @returns {string} - Custom label or formatted exposure type
     */
    getLabel: (state) => (exposureType) => {
      // Handle null or undefined exposureType
      if (!exposureType) {
        return ''
      }
      const customLabel = state.mappings[exposureType]
      if (customLabel && customLabel.trim()) {
        return customLabel.trim()
      }
      // Fallback to formatted exposure type
      return exposureType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    /**
     * Get a short/compact label for an exposure type (for tabs, sidebars, badges)
     * Falls back to getLabel if no short form exists
     * @param {string|null} exposureType - The exposure type
     * @returns {string} - Short label
     */
    getShortLabel: (state) => (exposureType) => {
      const SHORT_LABELS = {
        'company_internal_idiosyncratic_risks': 'Idiosyncratic Risks',
        'gdp_growth/contraction_trends': 'GDP Growth',
        'consolidation_&_m&a': 'M&A',
        'interest_rate': 'Interest Rate',
        'supply_chain': 'Supply Chain',
        'tariff': 'Tariff'
      }
      if (!exposureType) return ''
      if (SHORT_LABELS[exposureType]) return SHORT_LABELS[exposureType]
      // Fall back to custom label or formatted name
      const customLabel = state.mappings[exposureType]
      if (customLabel && customLabel.trim()) return customLabel.trim()
      return exposureType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    /**
     * Check if we have any custom mappings loaded
     */
    hasCustomMappings: (state) => {
      return Object.keys(state.mappings).length > 0
    },

    /**
     * Check if data needs refresh (older than 5 minutes)
     */
    needsRefresh: (state) => {
      if (!state.lastLoaded) return true
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
      return state.lastLoaded < fiveMinutesAgo
    }
  },

  actions: {
    /**
     * Load label mappings from the backend
     * @param {boolean} force - Force reload even if recently loaded
     */
    async loadMappings(force = false) {
      // Skip if recently loaded and not forcing
      if (!force && !this.needsRefresh && this.hasCustomMappings) {
        return { success: true, fromCache: true }
      }

      this.isLoading = true
      this.error = null

      try {
        const response = await labelMappingService.getLabelMappings()
        
        if (response.success) {
          this.allMappings = Array.isArray(response.mappings) ? response.mappings : []
          
          // Use the most recent mapping as active mappings
          if (this.allMappings.length > 0) {
            const mostRecent = this.allMappings[0]
            this.mappings = mostRecent.mapping || {}
          } else {
            this.mappings = {}
          }
          
          this.lastLoaded = Date.now()
          return { success: true, mappings: this.mappings }
        } else {
          this.error = 'Failed to load label mappings'
          return { success: false, error: this.error }
        }
      } catch (error) {
        console.error('Error loading label mappings:', error)
        this.error = error.response?.data?.detail || 'Network error occurred while loading mappings'
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update mappings after successful backend save
     * @param {Object} newMappings - The new mappings object
     */
    updateMappings(newMappings) {
      this.mappings = { ...newMappings }
      this.lastLoaded = Date.now()
      this.error = null
    },

    /**
     * Save label mappings to backend and update store (always creates/updates single mapping per user)
     * @param {Object} mappingToSave - The mappings to save
     * @param {number|null} editingId - ID if editing existing mapping (ignored, kept for compatibility)
     */
    async saveMappings(mappingToSave, editingId = null) {
      this.isUpdating = true
      this.error = null

      try {
        // Always use POST endpoint since it now handles upsert
        const response = await labelMappingService.saveLabelMapping(mappingToSave)

        if (response.success) {
          // Update the store with new mappings
          this.updateMappings(mappingToSave)
          
          // Reload all mappings to get updated list
          await this.loadMappings(true)
          
          return { success: true }
        } else {
          this.error = 'Failed to save label mapping'
          return { success: false, error: this.error }
        }
      } catch (error) {
        console.error('Error saving label mappings:', error)
        this.error = error.response?.data?.detail || 'Network error occurred while saving mapping'
        return { success: false, error: this.error }
      } finally {
        this.isUpdating = false
      }
    },

    /**
     * Clear all mappings and reset state
     */
    clearMappings() {
      this.mappings = {}
      this.allMappings = []
      this.error = null
      this.lastLoaded = null
    },

    /**
     * Format a single exposure type (utility method)
     * @param {string|null} exposureType - The exposure type to format
     * @returns {string} - Formatted exposure type
     */
    formatExposureType(exposureType) {
      // Handle null or undefined exposureType
      if (!exposureType) {
        return ''
      }
      return exposureType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }
})
